import React from 'react';
import Moment from 'moment';
import { Box, type BoxProps, type SxProps, type Theme } from '@mui/material';
import { useParams } from 'react-router-dom';
import DatePicker from '../../../customizeQuote/components/DatePicker';
import { toUniversalUtcDate } from '../../../../../../../utils/date.utils';
import useEffectiveDateConstraints from './useEffectiveDateConstraints';
import { Show } from '../../../../../../../components/Show';
import type { Nullable } from '../../../../../../../types/common/utility';
import {
  ProductTypes,
  type PrimeXProductType,
} from '../../../../../../../types';

interface CoverageDatesProps {
  readonly endDate: Nullable<string>;
  readonly effectiveDate: Nullable<string>;
  readonly setEndDate?: (date: string) => void;
  readonly setEffectiveDate: (date: string) => void;
  readonly startLabel?: string;
  readonly endLabel?: string;
  readonly hidePolicyTerm?: boolean;
  readonly minEffectiveDate?: Date;
  readonly maxEffectiveDate?: Date;
  readonly isRenewal?: boolean;
  readonly disableEndDate?: boolean;
  /** Typography for the two date labels; defaults to the input label style. */
  readonly labelSx?: SxProps<Theme>;
}

const CoverageDates = ({
  endDate,
  effectiveDate,
  setEndDate,
  setEffectiveDate,
  startLabel = 'Coverage Start Date',
  endLabel = 'Coverage End Date',
  hidePolicyTerm = false,
  isRenewal = false,
  disableEndDate = false,
  minEffectiveDate,
  maxEffectiveDate,
  labelSx,
  ...props
}: CoverageDatesProps & BoxProps) => {
  const policyTerm = policyTermDays(effectiveDate, endDate);

  const startDateConstraints = useEffectiveDateConstraints();
  const endDateConstraints = useEndDateConstraints(effectiveDate);

  const handleEffectiveDateChange = (date: string) => {
    setEffectiveDate(Moment(date).format('YYYY-MM-DD'));
    setEndDate?.(followingEndDate(date));
  };

  return (
    <Box display="flex" gap="1rem" {...props}>
      <Box>
        <DatePicker.ButtonLabel
          style={{
            padding: 0,
            lineHeight: 1,
          }}
          TypographyProps={{ sx: labelSx }}
          disabled={isRenewal}
        >
          {startLabel}
        </DatePicker.ButtonLabel>

        <DatePicker.PopOverState>
          {(popOverState) => {
            return (
              <Box>
                <DatePicker.Button
                  onClick={popOverState.onOpen}
                  style={{ minWidth: '10rem' }}
                  disabled={isRenewal}
                >
                  <span>
                    {toUniversalUtcDate(effectiveDate, {
                      placeholder: 'MM/DD/YYYY',
                    })}
                  </span>
                </DatePicker.Button>
                <DatePicker.PopOver {...popOverState}>
                  <DatePicker.Calendar
                    selected={Moment(effectiveDate).utc().toDate()}
                    {...startDateConstraints}
                    minDate={minEffectiveDate ?? startDateConstraints?.minDate}
                    maxDate={maxEffectiveDate ?? startDateConstraints?.maxDate}
                    onChange={(date: Date | null) => {
                      handleEffectiveDateChange(String(date));
                      popOverState.onClose();
                    }}
                  />
                </DatePicker.PopOver>
              </Box>
            );
          }}
        </DatePicker.PopOverState>
      </Box>

      <Box position="relative">
        <DatePicker.ButtonLabel
          style={{ padding: 0, lineHeight: 1 }}
          TypographyProps={{ sx: labelSx }}
          disabled={disableEndDate}
        >
          {endLabel}
        </DatePicker.ButtonLabel>

        <DatePicker.PopOverState>
          {(popOverState) => {
            return (
              <Box>
                <DatePicker.Button
                  onClick={popOverState.onOpen}
                  style={{ minWidth: '10rem' }}
                  disabled={disableEndDate}
                >
                  <span>
                    {toUniversalUtcDate(endDate, {
                      placeholder: 'MM/DD/YYYY',
                    })}
                  </span>
                </DatePicker.Button>
                <DatePicker.PopOver {...popOverState}>
                  <DatePicker.Calendar
                    selected={Moment(endDate).utc().toDate()}
                    {...endDateConstraints}
                    onChange={(date: Date | null) => {
                      setEndDate?.(Moment(date).format('YYYY-MM-DD'));
                      popOverState.onClose();
                    }}
                  />
                </DatePicker.PopOver>
              </Box>
            );
          }}
        </DatePicker.PopOverState>
        <Show when={!hidePolicyTerm}>
          <Box position="absolute">Policy Term: {policyTerm} days</Box>
        </Show>
      </Box>
    </Box>
  );
};

export default CoverageDates;

/** Days from the effective date to the end date, as every term label shows it. */
export const policyTermDays = (
  effectiveDate: Nullable<string>,
  endDate: Nullable<string>
): number => Moment.utc(endDate).diff(Moment.utc(effectiveDate), 'days');

/** The end date that follows an effective date: a year on, as both coverage pickers set it. */
export const followingEndDate = (effectiveDate: string): string =>
  Moment(effectiveDate).add(1, 'y').format('YYYY-MM-DD');

/**
 * The end dates a quote may take: from 6 months after it starts, 3 for Prime One, to 18 months after. Shared with the
 * broker experience's quote options.
 */
export function endDateBounds(
  effectiveDate: Nullable<string>,
  productType: PrimeXProductType | string | undefined
): { minDate: Date; maxDate: Date } {
  const minCoverageMonths = productType === ProductTypes.prime_one ? 3 : 6;
  return {
    minDate: Moment(effectiveDate).add(minCoverageMonths, 'months').toDate(),
    maxDate: Moment(effectiveDate).add(18, 'months').toDate(),
  };
}

export function useEndDateConstraints(effectiveDate: Nullable<string>) {
  const params = useParams<{
    productType?: PrimeXProductType;
    product?: PrimeXProductType;
  }>();
  const isPrimeOneProduct =
    params.productType === ProductTypes.prime_one ||
    params.product === ProductTypes.prime_one;
  const { minDate, maxDate } = endDateBounds(
    effectiveDate,
    isPrimeOneProduct ? ProductTypes.prime_one : undefined
  );
  const minTime = minDate.getTime();
  const maxTime = maxDate.getTime();

  return React.useMemo(
    () => ({ minDate: new Date(minTime), maxDate: new Date(maxTime) }),
    [minTime, maxTime]
  );
}
